import React, { useState } from 'react';
import { axios, API_URL, useDispatch, useSelector, toast, config } from '../../../Data/Imports';
import { getLabelTypeDropdown } from '../../../Redux/features/dropdown/dropdownSlice';

const ManageLabels = () => {
	const dispatch = useDispatch();
	const { labelTypeDropdown, isLoading } = useSelector((state) => state.dropdown);

	const [hasFetched, setHasFetched] = useState(false);
	if (labelTypeDropdown?.length === 0 && !isLoading && !hasFetched) {
		setHasFetched(true);
		dispatch(getLabelTypeDropdown());
	}

	const [labels, setLabels] = useState([]);
	const [LabelTypeId, setLabelTypeId] = useState(null);
	const [editMode, setEditMode] = useState(null);
	const [editedData, setEditedData] = useState({});

	const fetchLabels = async (LabelTypeId) => {
		setLabels([]);
		if (!LabelTypeId) return;
		try {
			const response = await axios.post(`${API_URL}/label/get/label`, { LabelTypeId });
			setLabels(response?.data?.Labels);
		} catch (error) {
			toast.error(error?.response?.data?.message || 'Something went wrong.');
		}
	};

	const toggleLabelStatus = (label) => {
		const updatedLabel = { ...label, IsActive: !label.IsActive };
		updateLabelData(updatedLabel);
	};

	const handleLabelTypeChange = (e) => {
		const newLabelTypeId = e.target.value;
		setLabelTypeId(newLabelTypeId);
		fetchLabels(newLabelTypeId);
	};

	const handleEditClick = (label) => {
		setEditMode(label.Id);
		setEditedData({ ...label });
	};

	const handleInputChange = (e, field) => {
		setEditedData({ ...editedData, [field]: e.target.value });
	};

	const handleCancelEdit = () => {
		setEditMode(null);
		setEditedData({});
	};

	const handleUpdateLabel = async () => {
		if (!editedData.LabelHeading || !editedData.LabelName) {
			toast.error('All fields are required!');
			return;
		}

		updateLabelData(editedData);
	};

	const updateLabelData = async (updatedLabel) => {
		try {
			await axios.post(`${API_URL}/label/post/update-label`, updatedLabel, config);
			setLabels((prevLabels) =>
				prevLabels.map((label) => (label.Id === updatedLabel.Id ? updatedLabel : label))
			);
			toast.success('Label updated successfully');
			setEditMode(null);
			setEditedData({});
		} catch (error) {
			toast.error(error?.response?.data?.message || 'Failed to update label.');
		}
	};

	return (
		<div className='w-full max-w-4xl mx-auto p-6 bg-white rounded-md shadow-lg mt-10'>
			<div>
				<label htmlFor='LabelTypeId' className='block text-sm font-medium text-gray-700 mb-2'>
					Label Type
				</label>
				<select
					id='LabelTypeId'
					name='LabelTypeId'
					value={LabelTypeId}
					onChange={handleLabelTypeChange}
					required
					className='w-full p-3 border-2 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'>
					<option value=''>Select a Label Type</option>
					{labelTypeDropdown?.map((labelType) => (
						<option key={labelType.Id} value={labelType.Id}>
							{labelType.MasterValue}
						</option>
					))}
				</select>
			</div>

			<div className='mt-4'>
				{labels?.length > 0 ? (
					<table className='w-full'>
						<thead>
							<tr className='bg-gray-100'>
								<th className='py-2 px-4 border text-center'>Label Heading</th>
								<th className='py-2 px-4 border text-center'>Label Name</th>
								<th className='py-2 px-4 border text-center'>Status</th>
								<th className='py-2 px-4 border text-center'>Actions</th>
							</tr>
						</thead>
						<tbody>
							{labels?.map((label) => (
								<tr key={label.Id}>
									<td className='py-2 px-4 border text-center'>
										{editMode === label.Id ? (
											<input
												type='text'
												value={editedData.LabelHeading}
												onChange={(e) => handleInputChange(e, 'LabelHeading')}
												className='border p-1 rounded w-full'
											/>
										) : (
											label.LabelHeading
										)}
									</td>
									<td className='py-2 px-4 border text-center'>
										{editMode === label.Id ? (
											<input
												type='text'
												value={editedData.LabelName}
												onChange={(e) => handleInputChange(e, 'LabelName')}
												className='border p-1 rounded w-full'
											/>
										) : (
											label.LabelName
										)}
									</td>
									<td className='py-2 px-4 border text-center'>
										<button onClick={() => toggleLabelStatus(label)}>
											<label className='relative inline-flex items-center cursor-pointer'>
												<input
													type='checkbox'
													value=''
													className='sr-only peer'
													checked={label.IsActive}
													onClick={(e) => e.stopPropagation()}
													readOnly
												/>
												<div
													className={`peer ring-0 bg-rose-400 rounded-full outline-none duration-300 after:duration-500 w-8 h-8 shadow-md peer-checked:bg-emerald-500 peer-focus:outline-none 
      after:rounded-full after:absolute after:outline-none after:h-6 after:w-6 after:bg-gray-50 
      after:top-1 after:left-1 after:flex after:justify-center after:items-center peer-hover:after:scale-75 
     ${label.IsActive ? 'after:content-["✔️"] after:rotate-0' : 'after:content-["✖️"] after:-rotate-180'}`}></div>
											</label>
										</button>
									</td>
									<td className='py-2 px-4 border text-center'>
										{editMode === label.Id ? (
											<div className='flex space-x-2'>
												<button
													onClick={handleUpdateLabel}
													className='px-4 py-2 bg-blue-500 text-white rounded-md cursor-pointer'>
													Save
												</button>
												<button
													onClick={handleCancelEdit}
													className='px-4 py-2 bg-gray-500 text-white rounded-md cursor-pointer'>
													Cancel
												</button>
											</div>
										) : (
											<button
												onClick={() => handleEditClick(label)}
												className='px-4 py-2 bg-yellow-500 text-white rounded-md cursor-pointer'>
												Edit
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<p>No labels found.</p>
				)}
			</div>
		</div>
	);
};

export default ManageLabels;
